import { Config } from 'config';

import { forEach, remove, now } from 'lodash';

import { concat, map$, invokeMap$, resolve } from 'gear';

import { Point, Color,
         Document, FigureConditions_Mixin } from 'cdl';

import { View, SyncedFigureViews, Plugin, findByTextureType,
         View2D_Mixin,
         DocumentView_Mixin,
         SelectedFigures_Mixin,
         InteractiveView_Mixin,
         TextureComposition, TextureTile } from '../view';

import { Doc3D_Model_Mixin } from './doc3d-model-mixin';

import { TextureViewComposition } from './texture-view-scene';

import { TextureTracker } from './move3d';



const fpsLimit = 6;

export const StaticTextureView = View.extend( View2D_Mixin           )
                                     .extend( DocumentView_Mixin     )
                                     .extend( SelectedFigures_Mixin  )
                                     .extend( FigureConditions_Mixin )
                                     .extend( Doc3D_Model_Mixin      ).extend({

  typeName: 'StaticTextureView',

  Properties: {

    document: { type: 'Document', def: null, owned: false, set( v ) { this._setDoc(v); } },

    canvas3D: { type: 'boolean', def: null, onChange(value) {
      if( value && this.model ) {
        this.zoomToFitTextureFor(this.model);
      }
    }},

    // [TODO] onChange, fireEvent
    backSideDarkFactor: { type: 'number', def: 0.5 },

    backSideColor: { type: 'Color', def: null, get() {
      return this._backSideColor || Color.changeLuminosity( this.frontSideColor, - this.backSideDarkFactor );
    }},

    textureSize: { type: 'number', def: null, onChange() {
      this.updateSize();
    } }
  },

  get model() {
    const { doc } = this;
    return doc && doc.model3D;
  },

  getThreeTexture( textureType ) {
    if( textureType === 'color' ) {
      return this.scene.threeTexture;
    }
    else {
      // Textures compositions, full size, same as color
      const texture = findByTextureType( this.textures, textureType );
      if( texture ) {
        return texture.threeTexture;
      }
      else {
        // Tiled textures
        const tiledTexture = this.textureTiles[ textureType ];
        if( tiledTexture ) {
          return tiledTexture.threeTexture;
        }
      }
    }
  },

  get frontSideColor() {
    return this.backgroundColor;
  },
  set frontSideColor( color ) {
    this.backgroundColor = color;
  },

  updateSize(w,h) {
    const { textureSize, width, height, _previewing } = this;
    if( textureSize && arguments.length === 0 ) {
      if( this.canvas3D ) {
        if( ! _previewing && ( textureSize !== width || textureSize !== height ) ) {
          // console.log('full texture');
          this.callParent(textureSize,textureSize);
        }
        else if( _previewing ) {
          if( textureSize === 512 ) {
            return;
          }
          let previewingTextureSize = Math.max( textureSize / ( this.stats.fps > fpsLimit ? 2 : 4 ) ,512);
          if( ! ( previewingTextureSize === width && previewingTextureSize === height ) ) {
            // console.log('preview texture ' + previewingTextureSize );
            this.callParent(previewingTextureSize,previewingTextureSize);
          }
        }
      }
      else {
        this.callParent(textureSize,textureSize);
      }
    }
    else {
      this.callParent(w,h);
    }
  },

  postResize() {
    if( this._modelReady ) {
      this.zoomToFitTextureFor( this.model );
    }
  },

  createScene() {
    return new TextureViewComposition({ main: true });
  },

  redrawFigure( figure ) {
    this.renderer.redraw( this, figure );
  },

  draw() {

    if( ! this._modelReady ) {
      return;
    }
    const { canvas3D } = this;
    if( canvas3D ) {
      canvas3D.computeViewToTextureScale();
    }
    if( this._previewing && now() - this._previewingT > 1200 ) {
      this.endPreview();
      this.render$();
      return;
    }
    this.callParent();
  },

  // [TODO] This should be available at the view level
  // This function will make sure that everything is ready to be rendered
  // before drawing. Is used in the 3D thumbnail generation process.
  draw$() {
    const { doc, zoom } = this;
    return doc.render$() .then( () => {
    return doc.prepareToDraw$(zoom) .then( () => {
    return invokeMap$( this.mapToFigureViews(doc.figures), 'ready$' ) .then( () => {
    return map$( this.textureTiles, 'promise' ) .then( () => {
    this.redrawAll();
    this.draw();
    });});});});
  },

  zoomToFitTextureFor( model ) {
    const { textureWidth } = model;
    const { width } = this;
    this.zoom   = width / textureWidth;
    this.center = new Point( textureWidth/2, textureWidth/2 );
    return this;
  },

  zoomToFitTexture$() {
    return this.loadModel3D$() .then( model => { // [TODO]
    if( model ) {
      return this.zoomToFitTextureFor(model);
    }
    });
  },

  zoomToFit$() {
    if( arguments.length === 0 ){
      return this.zoomToFitTexture$();
    }
    else {
      return this.zoomToFitFigures$(...arguments);
    }
  },

  docToView(/* docPoint */) {
    console.log('TextureView docToView not supported');
  },

  docDeltaToView( docDelta ) {
    return this.viewTransform.docDeltaToView(this,docDelta);
  },

  viewToDoc( viewPoint, docIsland ) {
    return this.modelToDoc(viewPoint,docIsland);
  },

  docPoint( event, docIsland ) {
    return this.modelToDoc( this.modelPoint(event), docIsland );
  },

  textureDeltaToDoc( textureDelta ) {
    return this.viewTransform.viewDeltaToDoc(this,textureDelta);
  },

  // Overwritten in TextureView below
  viewDeltaToDoc( viewDelta ) {
    return this.viewTransform.viewDeltaToDoc(this,viewDelta);
  },

  mapToFigureViews( figures ) {
    return this.figureViews.mapToFigureViews(figures);
  },

  figureView( figure ) {
    return this.figureViews.get(figure);
  },

  figureTextureTypes() {
    return [ 'color' ]; // Allow 'normal', 'bump', 'specular'...
  },

  init( config = {} ) {

    this.addEvents('synctextures');

    this._previewing = false;
    this._previewingT = 0;

    this._modelReady = false;
    this.figureViews = new SyncedFigureViews({ view: this });

    this.textureTiles = {};

    this._backgroundColor = Color.White();

    this.callParent(config);

    this._setDoc( new Document() );
  },

  insertViewPlugins( plugins ) {
    this.callParent(plugins);
    let textureViewPlugins = [
      Plugin.TextureMap({ textureType: 'color' }),
      Plugin.TextureViewRenderer()
    ];
    if( ! this.canvas3D ) {
      textureViewPlugins = concat(textureViewPlugins,[
        Plugin.ViewControls(),
        Plugin.PluginsLayer(),
        Plugin.Tools()
      ]);
    }
    plugins.insert(0,textureViewPlugins);
  },

  _setDoc( doc ) {

    const old = this.doc;
    if( old === doc ) {
      return resolve(); // Nothing to do
    }
    if( old ) {
      const { model3D } = old; // [TODO] This will be removed
      if( model3D ) {
        model3D.un(this);
      }

      this.unlinkDoc(old);
    }

    this._modelReady = false;

    this._document = doc;
    this.linkDoc(doc);

    if( ! doc.is3D ) {
      return resolve();
    }

    return doc.loadModel3D$() .then( () => {
    // [TODO] We could avoid this by avoiding async processing for simple shapes
    // This needs further research

    /*
    // We are only updating when the Doc3D fires events
    doc.model3D.on({
      'materialschanged': this.on_materialschanged,
      'model3dchanged': this.on_model3dchanged,
      'modeldata3dchanged': this.on_modeldata3dchanged,
    },this);
    */

    return doc.model3D.update$() .then( () => {


    this.syncModel3D();

    this._modelReady = true;
    // console.log('modelReady');

    }); });
  },

  linkDoc( doc ) {
    this.figureViews.link(doc);
    this.callParent(doc);
    this.fireEvent('newdocument',this,doc);
  },

  unlinkDoc( doc ) {
    this.figureViews.unlink(doc);
  },

  loadModel3D$() {
    return this.doc.is3D ? this.doc.loadModel3D$() : resolve();
  },

  syncTextures() {

    // Sync full textures, color and optinal maps
    // The idea is that each figure should be able to draw to these textures
    // Tiled textures are directly loaded and cached by getThreeTexture

    if( Config.ignoreTextures ) {
      return;
    }

    const { textures } = this;

    // Sync texture backgrounds plugins
    const { plugins } = this;
    remove( plugins, plugin => {
      return ( plugin instanceof Plugin.TextureMap ) && plugin.textureType !== 'color';
    });

    findByTextureType( plugins, 'color' ).updateMap();

    forEach( ['bump','normal','specular','displacement','alpha'], textureType => {

      // This function ignores bump maps if there are normal maps
      const textureMap = this.getFixedTextureMap(textureType);

      const texture = findByTextureType(textures,textureType);
      if( textureMap && textureMap.wrap === 'Stretch' ) { // [TODO] Enable it if figures want to draw things to this textureType
        if( ! texture ) {
          const composition = this.createTexture(textureType);
          textures.push(composition);
        }
        plugins.unshift( Plugin.TextureMap({ textureType }) );
      }
      else if( texture ) {
        if( texture.layersContainer ) { // [TODO]
          texture.remove();
        }
        textures.remove( texture );
      }

      const { textureTiles } = this;
      const tiledTexture = textureTiles[textureType];
      if( textureMap && textureMap.wrap === 'Repeat' ) {
        if( ! tiledTexture || tiledTexture.id !== textureMap.id ) {

          textureTiles[textureType] = new TextureTile(this,textureType,textureMap);
        }
      }
      else {
        textureTiles[textureType] = null;
      }
    });

    this.fireEvent('synctextures',this);
  },

  getFixedTextureMap( textureType ) {
    const { model, doc } = this;
    const docMaterial = doc && doc.materials && doc.materials[0];
    if( textureType === 'normal' && ( docMaterial && docMaterial.getFixedTextureMap('bump') ) ) {
      // Ignore normal map in Model3D if there is a bump map in Doc3D
      return null;
    }
    else {
      const modelMaterial = model && model.materials[0];
      const docTextureMap = docMaterial && docMaterial.getFixedTextureMap(textureType);
      const modelTextureMap = modelMaterial && modelMaterial.getFixedTextureMap(textureType);
      return docTextureMap || modelTextureMap;
    }
  },

  createTexture( textureType ) {
    return new TextureComposition({ textureType });
  },

  syncModel3D() {
    // console.log('syncModel3D');

    this.syncTextures();

    if( this.canvas3D ){
      // Forces the texture to cover the requested textureSize ( 512, 1024, 2048 )
      this.zoomToFitTextureFor(this.model);
    }
  },

  on_materialschanged() {
    this.syncModel3D();
    this.callParent(...arguments);
  },

  on_model3dchanged() {
    this.syncModel3D();
    this.callParent(...arguments);
  },
  /*
  on_modeldata3dchanged() {

  },
  */

  optimizePreview() {
    if( Config.texturePreview ) {
      this._previewingResize = true;
      this._previewing = true;
      this._previewingT = now();
      this.updateSize();
      this._previewingResize = false;
    }
  },
  endPreview() {
    if( this._previewing && this.width !== this.textureSize ) {
      this._previewingResize = true;
      this._previewing = false;
      this.updateSize();
      this._previewingResize = false;
    }
  },

  // [MODEL]
  on_renderstarted( doc, type ) {
    if( this.canvas3D ) {
      if( type === 'preview' ) {
        this.optimizePreview();
      }
      else if( type === 'commit' ) {
        this.endPreview();
      }
    }
    this.callParent(doc,type);
  },

  dispose() {
    if( this.doc ) {
      this.unlinkDoc(this.doc);
    }
    this.callParent();
  },

  onBackgroundColorChange() {
    this.redraw('document');
  },

  get textureView() {
    return this;
  },

  pluginDirectDomEventHandling({ target }) {
    return target === 'view' || target === 'model';
  },

  createEventTracker( event, docIsland ) {
    return new TextureTracker(event,docIsland);
  }

});

export const TextureView = StaticTextureView.extend( InteractiveView_Mixin ).extend({

  typeName: 'TextureView',

  // Rewire user interaction to Canvas3D if this texture is being used for it
  // Plugins are right now added to the TextureView, because the rendering code
  // looked complex to abstract. It may be a good idea to let the plugins in the
  // 3D view and wire the rendering instead. This starts to look really complex.

  get is3D() {
    return !!this.canvas3D;
  },

  get selectAndDragEnabled() {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.selectAndDragEnabled : false;
  },

  viewDeltaToDoc( delta ) {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.viewDeltaToDoc(delta) : this.callParent(delta);
  },

  viewDeltaToTexture( delta ) {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.viewDeltaToTexture(delta) : this.callParent(delta);
  },

  triggerKeyboard(event) {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.triggerKeyboard(event) : this.callParent(event);
  },

  are() {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.are(...arguments) : this.callParent(...arguments);
  },

  is() {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.is(...arguments) : this.callParent(...arguments);
  },

  /*
  condition() {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.condition(...arguments) : this.callParent(...arguments);
  },
  */

  status(s) {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.status(s) : this.callParent(s);
  },

  get_cursor() {
    const { canvas3D } = this;
    return canvas3D ? canvas3D.cursor : this.callParent();
  },

  set_cursor( cursor ) {
    const { canvas3D } = this;
    if( canvas3D ) {
      canvas3D.cursor = cursor;
    }
    else {
      this.callParent(cursor);
    }
  },

  fireEvent( type, ...params ) {
    const { canvas3D } = this;
    if( canvas3D ) {
      if( type === 'toolchanged' ||
          type === 'figuresdrag' ||
          type === 'figuresdrop'  ) {

        canvas3D.fireEvent(type,...params);
      }
    }
    this.callParent(type,...params);
  },

});


export default TextureView;



/** WEBPACK FOOTER **
 ** ./src/ui/canvas3d/texture-view.js
 **/